import { useEffect, useRef, useCallback, useState } from "react";
import notificationWebSocketService from "../services/notificationWebSocketService";
import logger from "../utils/logger";

/**
 * Custom hook for WebSocket notification updates with pagination
 * @param {Function} onNotification - Callback when notification is received
 * @param {Function} onNotificationsLoaded - Callback for batch notifications (receives { notifications, pagination, isLoadMore })
 * @returns {Object} { isConnected, loadMore, hasMore, isLoadingMore }
 */
const useNotificationWebSocket = (onNotification, onNotificationsLoaded = null) => {
  // Store subscription reference for cleanup
  const subscriptionRef = useRef(null);
  // Store the latest callbacks in refs to avoid recreating subscriptions
  const callbackRef = useRef(onNotification);
  const loadedCallbackRef = useRef(onNotificationsLoaded);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Update the callback refs when they change
  useEffect(() => {
    callbackRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    loadedCallbackRef.current = onNotificationsLoaded;
  }, [onNotificationsLoaded]);

  // Stable callback that reads from ref
  const handleNotification = useCallback(notification => {
    logger.log("🔔 Notification received:", notification);
    if (callbackRef.current) {
      callbackRef.current(notification);
    }
  }, []);

  // Stable callback for batch notifications (initial load)
  const handleNotificationsLoaded = useCallback(({ notifications, pagination }) => {
    logger.log(`📬 Notifications loaded: ${notifications.length}, hasNext: ${pagination.hasNext}`);
    setCurrentPage(pagination.page);
    setHasMore(pagination.hasNext);

    if (loadedCallbackRef.current) {
      loadedCallbackRef.current({ notifications, pagination, isLoadMore: false });
    }
  }, []);

  // Load more notifications (next page)
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) {
      return;
    }

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const result = await notificationWebSocketService.fetchNotifications(nextPage, 15);
      const { notifications, pagination } = result;

      setCurrentPage(pagination.page);
      setHasMore(pagination.hasNext);

      if (loadedCallbackRef.current && notifications.length > 0) {
        loadedCallbackRef.current({ notifications, pagination, isLoadMore: true });
      }
    } catch (error) {
      logger.error("Failed to load more notifications:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, hasMore, isLoadingMore]);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_WS_URL || process.env.REACT_APP_BASE_URL || "https://api.spacia.africa/";
    const token = localStorage.getItem("access_token") || localStorage.getItem("authToken");

    if (!token) {
      logger.log("⚠️ No auth token available for notification WebSocket");
      return;
    }

    notificationWebSocketService
      .connect(baseUrl, token)
      .then(() => {
        logger.log("Notification WebSocket connected");
        // Subscribe and store reference for cleanup
        // Pass both the notification callback and batch notifications callback
        subscriptionRef.current = notificationWebSocketService.subscribeToNotifications(
          handleNotification,
          handleNotificationsLoaded
        );
      })
      .catch(_err => {
        logger.log("⚠️ Notification WebSocket connection failed:", _err.message);
      });

    // Cleanup: unsubscribe this component's callback on unmount
    // This removes the component's callback but keeps the connection alive
    return () => {
      if (subscriptionRef.current && subscriptionRef.current.unsubscribe) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
        logger.log("Cleaned up notification WebSocket subscription");
      }
    };
  }, [handleNotification, handleNotificationsLoaded]);

  return {
    isConnected: notificationWebSocketService.isConnected(),
    loadMore,
    hasMore,
    isLoadingMore,
  };
};

export default useNotificationWebSocket;
